import { PageLoader } from '@/components/ui/loader'
import UnderVerification from '@/components/under-verification'
import { endPoint } from '@/constant/end-point'
import useAPI from '@/hooks/use-api'
import { useMe } from '@/hooks/use-me'
import type { IMe } from '@/interface/auth.interface'
import { revokeCurrentServerSession } from '@/utils/session-actions'
import { UserPreferenceProvider } from '@/providers/user-preference.provider'
import { UserProvider } from '@/providers/user-provider'
import { useAuth, useClerk } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { Navigate } from 'react-router'

interface ProtectedRouteProps {
  children: React.ReactNode
}

function ProtectedRouteContent({ children }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded } = useAuth()
  const { signOut } = useClerk()
  const { getOne: getMe } = useAPI<IMe>()
  const { setMe } = useMe()

  const { data: me, isLoading: isMeLoading } = useQuery({
    queryKey: ['getMe'],
    queryFn: async () => {
      try {
        const response = await getMe(endPoint?.me, {})
        if (!response?.status) throw new Error(response?.message)
        return {
          data: response?.data,
        }
      } catch (error) {
        if (isSignedIn) {
          try {
            await revokeCurrentServerSession()
          } catch {
            // Continue clearing client session even if server revocation fails.
          }
          await signOut({ redirectUrl: '/sign-in' })
        }
        throw error
      }
    },
    enabled: isLoaded && isSignedIn,
    retry: false,
  })

  // Set me data in context when it's available
  React.useEffect(() => {
    if (me) setMe(me?.data)
  }, [me, setMe])

  if (!isLoaded || (isSignedIn && isMeLoading)) return <PageLoader />

  if (!isSignedIn) return <Navigate to='/sign-in' replace />

  if (!me?.data?.approved || !me?.data?.active) return <UnderVerification />

  return <>{children}</>
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  return (
    <UserProvider>
      <ProtectedRouteContent>
        <UserPreferenceProvider>{children}</UserPreferenceProvider>
      </ProtectedRouteContent>
    </UserProvider>
  )
}
